import { Box } from '@chakra-ui/react';
import { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import { cloneElement, ReactElement } from 'react';

import { RouteProps } from '@/components/RouteLink';
import { RegistryPackageDocsPage } from '@/graphql/upbound-graphql';

import DocsTreeExpander from './DocsTreeExpander';
import DocsTreeLink from './DocsTreeLink';

type ParentElement = ReactElement<{ title: string; titles?: Array<string>; children?: Array<ParentElement> }>;

function getExpanderVariant(index: number) {
  if (index < 1) {
    return 'root';
  }

  return 'child';
}

function recursivelyUpdateChildren({
  currentPage,
  selectedPage,
  params,
  node,
}: {
  currentPage: RegistryPackageDocsPage;
  selectedPage: RegistryPackageDocsPage | undefined;
  params: Params<RouteProps['packageDocsTab']>;
  node: ParentElement;
}): Array<EmotionJSX.Element> {
  const titleParts = currentPage.name.split('/');
  const [firstTitlePart] = titleParts;
  const [lastTitlePart] = titleParts.splice(-1);
  const children = node.props.children || [];
  const updatedNodeIndex = children.findIndex(child => child.props.title === firstTitlePart);

  if (updatedNodeIndex < 0) {
    // New node
    return [
      ...children,
      titleParts.reduce(
        (child, titlePart, index) => (
          <DocsTreeExpander
            key={titlePart}
            title={titlePart}
            titles={[currentPage.name]}
            selectedPage={selectedPage}
            variant={getExpanderVariant(index)}
          >
            {[child]}
          </DocsTreeExpander>
        ),
        <DocsTreeLink
          key={currentPage.location}
          title={currentPage.name}
          variant="child"
          params={params}
          selectedPage={selectedPage}
        >
          {lastTitlePart}
        </DocsTreeLink>,
      ),
    ];
  }

  // Existing node
  const [, ...remainingTitleParts] = titleParts;
  const child = children[updatedNodeIndex];
  const titles = child.props.titles || [];

  return [
    ...children.slice(0, updatedNodeIndex),
    cloneElement(child, {
      ...child.props,
      titles: [...titles, currentPage.name],
      children: recursivelyUpdateChildren({
        currentPage: {
          ...currentPage,
          name: [...remainingTitleParts, lastTitlePart].join('/'),
        },
        selectedPage,
        params,
        node: child,
      }),
    }),
    ...children.slice(updatedNodeIndex + 1),
  ];
}

const DocsTree: React.FC<{
  tableOfContents: Array<RegistryPackageDocsPage>;
  selectedPage: RegistryPackageDocsPage | undefined;
  incompleteParams: Params<RouteProps['packageDocsTab']>;
}> = ({ tableOfContents, selectedPage, incompleteParams }) => {
  return tableOfContents.reduce<EmotionJSX.Element>((node, currentPage) => {
    const params = { ...incompleteParams, pageName: currentPage.name };
    if (currentPage.name.includes('/')) {
      // Nested link
      return cloneElement(node, {
        ...node.props,
        children: recursivelyUpdateChildren({
          currentPage,
          selectedPage,
          params,
          node,
        }),
      });
    }

    const children = node.props.children || [];

    const gtmClass = `provder-${currentPage.name.split(' ').join('').toLowerCase()}-tab`;
    // Root link
    return cloneElement(node, {
      ...node.props,
      children: [
        ...children,
        <DocsTreeLink
          className={gtmClass}
          key={currentPage.name}
          title={currentPage.name}
          variant="root"
          params={params}
          selectedPage={selectedPage}
        >
          {currentPage.name}
        </DocsTreeLink>,
      ],
    });
  }, <Box role="TreeOfContents" />);
};

export default DocsTree;
